/**
 * Common SanMar colors offered as type-down suggestions on the SKU
 * setup page, plus the matching logic behind that picker.
 *
 * WHERE THIS LIST CAME FROM. It is DERIVED FROM REAL SUPPLIER DATA, not
 * invented. Each entry is a (code, name, hex) triple assembled from three
 * files already in the repo:
 *
 *   * `name` and `code` -- the `COLOR_NAME` and `SANMAR_MAINFRAME_COLOR`
 *     columns of `api/src/integration_resources/sanmar/SanMar_EPDD.csv`,
 *     the same pairing `color_converter.csv` encodes as a code->name map.
 *   * ordering -- descending count of DISTINCT EPDD rows carrying that
 *     color, so the list is genuinely "the most common colors we see on
 *     SanMar" rather than an alphabetical slice.
 *   * `hex` -- `GENERIC_COLOR_HEX_FALLBACK` in
 *     `api/src/integration_resources/sanmar/color_hex_fallback.py`, which
 *     is already keyed in resolved-English-name space and already mirrors
 *     the `vendor_color_hex` seed fixtures. Reusing it means a suggested
 *     swatch and a real SanMar swatch for the same name agree.
 *
 * HEATHERS ARE EXCLUDED deliberately (Jake: "the most common colors
 * (non-heather)"). Phil's AdventHealth matrix bears that out -- 27
 * distinct colors across the event and only THREE heathers.
 *
 * THE LIST IS A STARTING POINT, NEVER A CONSTRAINT. The same matrix
 * contains `Polished`, `Atlas`, `Charter Blue` and one color literally
 * named `Pending`, none of which are SanMar names. A closed list would
 * reject all four, so the picker MUST accept free-form entry; these
 * suggestions only save typing for the common case.
 *
 * CODE AND DISPLAY NAME ARE DIFFERENT IDENTIFIERS and must not be
 * collapsed. The EPDD proves it: `True Navy` ships under BOTH the
 * `True Navy` and `TrueNavy` mainframe codes, and `Black/ White` under
 * both `Black/White` and `Blk/White`. The code is what the supplier's
 * own systems and a purchase order use; the display name is what a
 * shopper sees and what our keys resolve on.
 */
import { canonicalColorSkuKey } from "@/utils/colorSkuKey";

/**
 * @typedef {object} ColorSuggestion
 * @property {string} code supplier / mainframe color code ("DeepBlack")
 * @property {string} name human-readable display name ("Deep Black")
 * @property {string} hex  swatch hex ("#0A0A0A")
 */

/** @type {ColorSuggestion[]} */
export const COMMON_SANMAR_COLORS = [
    { code: "Black", name: "Black", hex: "#000000" },
    { code: "White", name: "White", hex: "#FFFFFF" },
    { code: "Navy", name: "Navy", hex: "#1F2A44" },
    { code: "Royal", name: "Royal", hex: "#05397B" },
    { code: "Charcoal", name: "Charcoal", hex: "#3A3B3C" },
    { code: "Red", name: "Red", hex: "#C8102E" },
    { code: "DeepBlack", name: "Deep Black", hex: "#0A0A0A" },
    { code: "True Navy", name: "True Navy", hex: "#1A2B5F" },
    { code: "True Royal", name: "True Royal", hex: "#1E3F8C" },
    { code: "Maroon", name: "Maroon", hex: "#5A1D2C" },
    { code: "Purple", name: "Purple", hex: "#33295E" },
    { code: "Jet Black", name: "Jet Black", hex: "#0B0B0B" },
    { code: "Gold", name: "Gold", hex: "#F6A21C" },
    { code: "Orange", name: "Orange", hex: "#E2581E" },
    { code: "Forest Green", name: "Forest Green", hex: "#21462E" },
    { code: "Light Blue", name: "Light Blue", hex: "#ADD8E6" },
    { code: "Carolina Blue", name: "Carolina Blue", hex: "#6395CA" },
    { code: "Grey", name: "Grey", hex: "#8A8F94" },
    { code: "Cardinal", name: "Cardinal", hex: "#721B24" },
    { code: "Yellow", name: "Yellow", hex: "#FEDB00" },
    { code: "Kelly Green", name: "Kelly Green", hex: "#0F8469" },
    { code: "Grey Steel", name: "Grey Steel", hex: "#7C8087" },
    { code: "Sand", name: "Sand", hex: "#D9C7A3" },
    { code: "Safety Green", name: "Safety Green", hex: "#C2E812" },
    { code: "Stone", name: "Stone", hex: "#CABFA3" },
    { code: "Khaki", name: "Khaki", hex: "#B7A98B" },
    { code: "SftyOrg", name: "Safety Orange", hex: "#FF6A13" },
    { code: "Pink", name: "Pink", hex: "#E5779A" },
    { code: "Steel Grey", name: "Steel Grey", hex: "#6E7479" },
    { code: "Texas Orange", name: "Texas Orange", hex: "#BF5700" },
    { code: "Sky Blue", name: "Sky Blue", hex: "#8AB6D6" },
    { code: "Blue", name: "Blue", hex: "#1E3A8C" },
    { code: "Brown", name: "Brown", hex: "#5A4534" },
    { code: "Green", name: "Green", hex: "#1F8A4C" },
    { code: "StrongNavy", name: "Strong Navy", hex: "#1B2A48" },
    { code: "Chocolate", name: "Chocolate", hex: "#40362D" },
    { code: "Hunter", name: "Hunter", hex: "#2C5234" },
    { code: "Athletic Red", name: "Athletic Red", hex: "#B22234" },
    { code: "Ath Gold", name: "Athletic Gold", hex: "#E59A0B" },
    { code: "Gray", name: "Gray", hex: "#8A8F94" },
];

/**
 * Filter the suggestion list for a type-down query.
 *
 * Matching is deliberately LOOSER than the identity key: it folds case
 * and whitespace so that typing "deep bl" or "DEEPBL" both reach
 * "Deep Black". That is a MATCH key, not an identity key -- the value
 * actually stored is always the suggestion's exact `name`. (Same split
 * as `normalizeColorNameForMatch` vs `canonicalColorSkuKey`; see
 * utils/colorSkuKey.js.)
 *
 * The supplier CODE is searched alongside the name, because someone
 * working from a supplier's own paperwork has the code in hand, not the
 * English name -- that is the whole reason `color_converter.csv` exists.
 *
 * An empty query returns the head of the list rather than nothing, so
 * focusing the field offers the most common colors immediately.
 *
 * @param {string} query raw text as typed
 * @param {number} [limit] max suggestions to return
 * @returns {ColorSuggestion[]}
 */
export function filterColorSuggestions(query, limit = 8) {
    const fold = (s) => String(s || "").replace(/\s+/g, "").toLowerCase();
    const q = fold(query);
    if (!q) return COMMON_SANMAR_COLORS.slice(0, limit);
    const out = [];
    for (const c of COMMON_SANMAR_COLORS) {
        if (fold(c.name).includes(q) || fold(c.code).includes(q)) {
            out.push(c);
            if (out.length >= limit) break;
        }
    }
    return out;
}

/**
 * Resolve raw typed text into the value to STORE.
 *
 * Canonicalizes the display name through `canonicalColorSkuKey` -- the
 * existing shared canonicalizer, byte-identical to the backend's
 * `normalize_display_whitespace` -- so "Athletic Heather " and
 * "Athletic Heather" resolve to one key and the blank-photo lookup
 * cannot silently miss.
 *
 * If the canonical name matches a known suggestion (case-insensitively),
 * its supplier code and hex come along. Otherwise the name is kept
 * verbatim with an EMPTY code: a free-form color genuinely has no
 * supplier code, and inventing one would be worse than leaving it blank
 * for a human to fill in from the supplier's paperwork.
 *
 * Slash order is MEANINGFUL and preserved -- `Navy/White` and
 * `White/Navy` are different colors on the same style (Cutter & Buck
 * LQK00041 in Phil's AdventHealth matrix). Nothing here reorders or
 * dedupes around the slash.
 *
 * @param {string} rawName text as typed
 * @returns {{name: string, code: string, hex: string|null}}
 */
export function resolveTypedColor(rawName) {
    const name = canonicalColorSkuKey(rawName);
    if (!name) return { name: "", code: "", hex: null };
    const hit = COMMON_SANMAR_COLORS.find(
        (c) => c.name.toLowerCase() === name.toLowerCase()
    );
    if (hit) return { name: hit.name, code: hit.code, hex: hit.hex };
    return { name, code: "", hex: null };
}
